// src/features/auth/queries/workspaceMembersQueries.ts
import { useQuery } from '@tanstack/react-query';
import { authQueryKeys } from './authQueries';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { MemberInfo } from '../types';
import { normalizeWorkspaceRole, normalizeWorkspaceStatus } from '../utils/workspaceRoleStatus';

// ワークスペースメンバー一覧を取得するクエリ関数
const fetchWorkspaceMembers = async (workspaceId: string): Promise<Map<string, MemberInfo>> => {
  try {
    const membersRef = collection(db, `workspaces/${workspaceId}/members`);
    const membersQuery = query(membersRef, orderBy('joinedAt', 'desc'));
    const membersSnapshot = await getDocs(membersQuery);

    const membersMap = new Map<string, MemberInfo>();

    membersSnapshot.docs.forEach((doc) => {
      const memberData = doc.data();
      const memberInfo: MemberInfo = {
        userId: doc.id,
        role: normalizeWorkspaceRole(memberData.role),
        status: normalizeWorkspaceStatus(memberData.status),
        joinedAt: memberData.joinedAt?.toDate() || new Date(),
      };
      membersMap.set(doc.id, memberInfo);
    });

    return membersMap;
  } catch (error) {
    console.error('❌ [WorkspaceMembers] メンバー一覧取得エラー:', {
      workspaceId,
      error: error instanceof Error ? error.message : String(error)
    });
    return new Map<string, MemberInfo>();
  }
};

/**
 * ワークスペースメンバー一覧を取得するクエリフック
 * 既存の authStore.workspaceMembers の代替として使用
 */
export const useWorkspaceMembersQuery = (workspaceId: string, enabled: boolean = true) => {
  return useQuery<Map<string, MemberInfo>>({
    queryKey: authQueryKeys.workspaceMembers(workspaceId),
    queryFn: () => fetchWorkspaceMembers(workspaceId),
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5分
    gcTime: 30 * 60 * 1000,   // 30分
    retry: 1,
  });
};

/**
 * ワークスペースメンバー一覧を配列形式で取得するクエリフック
 * UI表示用の便利なフック
 */
export const useWorkspaceMembersListQuery = (workspaceId: string, enabled: boolean = true) => {
  const { data: membersMap, ...rest } = useWorkspaceMembersQuery(workspaceId, enabled);
  
  return {
    ...rest,
    data: membersMap ? Array.from(membersMap.entries()).map(([userId, memberInfo]) => ({
      ...memberInfo,
      userId
    })) : [],
  };
};
